import { Dialog } from '@headlessui/react';
import { Copy, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { storeCartItems } from '@/utils/cartStorage';
import { toast } from 'react-hot-toast';
import { formatPrice } from '@/lib/utils';
import { CartItem } from '@/types';
import { calculateCartItemDiscount } from '@/lib/discount';
import { useNavigate } from 'react-router-dom';

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  paymentMethod: 'bank' | 'paystack' | 'monnify' | 'stripe';
  onPaymentMethodChange: (method: 'bank' | 'paystack' | 'monnify' | 'stripe') => void;
  onConfirm: () => void;
  storeInfo: any;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, type: 'order' | 'shipping') => void;
  orderTotal: number;
  shippingTotal: number;
  vatAmount: number;
  orderPaymentReceipt: File | null;
  shippingPaymentReceipt: File | null;
  cart?: CartItem[];
  customerEmail?: string;
  customerName?: string;
  isLoading?: boolean;
}

export function PaymentMethodDialog({
  open,
  onClose,
  paymentMethod,
  onPaymentMethodChange,
  onConfirm,
  storeInfo,
  onFileUpload,
  orderTotal,
  shippingTotal,
  vatAmount,
  orderPaymentReceipt,
  shippingPaymentReceipt,
  cart = [],
  customerEmail,
  customerName,
  isLoading = false,
}: PaymentMethodDialogProps) {
  const navigate = useNavigate();
  
  const handleCopyAccountNumber = (accountNumber: string) => {
    navigator.clipboard.writeText(accountNumber);
    toast.success('Account number copied to clipboard');
  };

  const showSeparateShipping = storeInfo?.mb_active === 1 && storeInfo?.sepShipping === 1 && shippingTotal > 0;
  const total = orderTotal + shippingTotal + vatAmount;

  // Calculate discount information
  const cartTotals = cart.reduce((totals, item) => {
    const { totalOriginalPrice, totalDiscountValue, totalFinalPrice } = calculateCartItemDiscount(item);
    return {
      originalSubtotal: totals.originalSubtotal + totalOriginalPrice,
      totalDiscount: totals.totalDiscount + totalDiscountValue,
      finalSubtotal: totals.finalSubtotal + totalFinalPrice
    };
  }, { originalSubtotal: 0, totalDiscount: 0, finalSubtotal: 0 });

  const hasDiscounts = cartTotals.totalDiscount > 0;

  const waitForStripe = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (typeof (window as any).Stripe !== 'undefined') {
        resolve((window as any).Stripe);
        return;
      }

      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      const checkStripe = () => {
        attempts++;
        if (typeof (window as any).Stripe !== 'undefined') {
          resolve((window as any).Stripe);
        } else if (attempts >= maxAttempts) {
          reject(new Error('Stripe.js failed to load. Please refresh the page and try again.'));
        } else {
          setTimeout(checkStripe, 100);
        }
      };
      checkStripe();
    });
  };

  const handleStripePayment = async () => {
    const loadingToast = toast.loading('Processing payment...');
    try {
      if (!storeInfo?.StripeKey) {
        throw new Error('Stripe is not properly configured for this store. Please contact the store administrator.');
      }

      // Wait for Stripe.js to load
      const StripeConstructor = await waitForStripe();
      
      // Create payment intent through our Node.js backend
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/payments/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(total * 100), // Convert to cents
          currency: 'usd', // Use USD as requested
          storeId: storeInfo.id,
          customerInfo: {
            name: customerName || 'Customer',
            email: customerEmail || '',
            phone: '',
            address: '',
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create payment intent');
      }

      const { clientSecret, paymentIntentId } = await response.json();

      if (!clientSecret) {
        throw new Error('Failed to initialize payment. Please try again.');
      }

      // Store complete order data for later use including all necessary info for order creation
      const orderData = {
        cart: cart,
        total: total,
        orderTotal: orderTotal,
        shippingTotal: shippingTotal,
        vatAmount: vatAmount,
        customerName,
        customerEmail,
        storeInfo: storeInfo,
        paymentIntentId: paymentIntentId
      };
      
      // Store cart items separately for reliable access using utility function
      storeCartItems(cart, total, orderTotal);
      localStorage.setItem('pendingStripeOrder', JSON.stringify(orderData));

      // Navigate to Stripe payment page with client secret
      navigate(`/stripe-payment?client_secret=${clientSecret}`);

      toast.dismiss(loadingToast);

    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Stripe payment error:', error);
      
      let errorMessage = 'Payment processing failed. ';
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again or contact support if the issue persists.';
      }
      
      toast.error(errorMessage);
    }
  };

  const handlePayment = () => {
    if (paymentMethod === 'stripe') {
      handleStripePayment();
    } else {
      onConfirm();
    }
  };

  const getButtonLabel = () => {
    if (paymentMethod === 'bank') {
      return 'Confirm Payment';
    }
    return `Pay ${formatPrice(total)}`;
  };

  const renderAmountDetails = () => (
    <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="font-semibold text-gray-900 dark:text-white">Order Summary</h3>
      </div>
      <div className="space-y-3">
        {hasDiscounts && (
          <>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Original Subtotal:</span>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 line-through">
                {formatPrice(cartTotals.originalSubtotal)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Bulk Discounts:</span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                -{formatPrice(cartTotals.totalDiscount)}
              </span>
            </div>
          </>
        )}
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {hasDiscounts ? 'Discounted Subtotal:' : 'Subtotal:'}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(orderTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-300">Shipping:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(shippingTotal)}</span>
        </div>
        {vatAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-300">TAX:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(vatAmount)}</span>
          </div>
        )}
        <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
          <div className="flex justify-between">
            <span className="text-base font-bold text-gray-900 dark:text-white">Total:</span>
            <span className="text-base font-bold text-gray-900 dark:text-white">{formatPrice(total)}</span>
          </div>
          {hasDiscounts && (
            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
              <div className="text-center">
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  🎉 You saved {formatPrice(cartTotals.totalDiscount)} with bulk discounts!
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-lg w-full max-h-[90vh] rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                Select Payment Method
              </Dialog.Title>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="max-h-[calc(100vh-16rem)] overflow-y-auto custom-scrollbar p-6">
            {renderAmountDetails()}
            
            <div className="space-y-4">
              {storeInfo?.mb_active === 1 && (
                <div className={`p-4 border-2 rounded-xl transition-all duration-200 cursor-pointer hover:shadow-md ${
                  paymentMethod === 'bank' 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm' 
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'
                }`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      value="bank"
                      checked={paymentMethod === 'bank'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'bank')}
                      className="mt-1 mr-4 w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="font-semibold text-gray-900 dark:text-white">Bank Transfer</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Transfer directly to our bank account</p>
                      {paymentMethod === 'bank' && (
                        <div className="mt-3 space-y-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Bank: <span className="font-bold">{storeInfo.m_bank}</span>
                          </p>

                          <div className="flex items-center space-x-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Account: <span className="font-bold">{storeInfo.m_ac_num}</span>
                            </p>
                            
                            <button
                              onClick={() => handleCopyAccountNumber(storeInfo.m_ac_name)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            >
                              <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            </button>
                          </div>

                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Account Name: <span className="font-bold">{storeInfo.m_ac_name}</span>
                          </p>
                            
                          {showSeparateShipping && (
                            <>
                              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">
                                  Shipping Amount: <b>{formatPrice(shippingTotal)}</b>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Shipping Payment Details:
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {storeInfo.sepShippingAcn}
                                </p>
                                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                                  Note: If shipping payment is combined with order payment, it will not be refunded and the order will not be shipped.
                                </p>
                              </div>
                            </>
                          )}
                          
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Upload Payment Receipt{showSeparateShipping ? ' (Order)' : ''}
                            </label>
                            <div className="flex items-center justify-center w-full">
                              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                orderPaymentReceipt 
                                  ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20' 
                                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                              }`}>
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                  <Upload className={`w-8 h-8 mb-4 ${
                                    orderPaymentReceipt ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                  }`} />
                                  {orderPaymentReceipt ? (
                                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                                      {orderPaymentReceipt.name}
                                    </p>
                                  ) : (
                                    <>
                                      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="font-semibold">Click to upload</span> or drag and drop
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        PNG, JPG or PDF (MAX. 2MB)
                                      </p>
                                    </>
                                  )}
                                </div>
                                <input 
                                  type="file" 
                                  className="hidden" 
                                  accept="image/*,.pdf"
                                  onChange={(e) => onFileUpload(e, 'order')}
                                />
                              </label>
                            </div>

                            {showSeparateShipping && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Upload Shipping Payment Receipt
                                </label>
                                <div className="flex items-center justify-center w-full">
                                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                    shippingPaymentReceipt 
                                      ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20' 
                                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                                  }`}>
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                      <Upload className={`w-8 h-8 mb-4 ${
                                        shippingPaymentReceipt ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                      }`} />
                                      {shippingPaymentReceipt ? (
                                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                                          {shippingPaymentReceipt.name}
                                        </p>
                                      ) : (
                                        <>
                                          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                          </p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            PNG, JPG or PDF (MAX. 2MB)
                                          </p>
                                        </>
                                      )}
                                    </div>
                                    <input 
                                      type="file" 
                                      className="hidden" 
                                      accept="image/*,.pdf"
                                      onChange={(e) => onFileUpload(e, 'shipping')}
                                    />
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              )}

              {storeInfo?.pays_active === 1 && (
                <div className={`p-4 border-2 rounded-xl transition-all duration-200 cursor-pointer hover:shadow-md ${
                  paymentMethod === 'paystack' 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-sm' 
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'
                }`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      value="paystack"
                      checked={paymentMethod === 'paystack'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'paystack')}
                      className="mt-1 mr-4 w-4 h-4 text-green-600 focus:ring-green-500 focus:ring-2"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <span className="font-semibold text-gray-900 dark:text-white">Pay with Paystack</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Secure card payments powered by Paystack
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {storeInfo?.monify_active === 1 && (
                <div className={`p-4 border-2 rounded-xl transition-all duration-200 cursor-pointer hover:shadow-md ${
                  paymentMethod === 'monnify' 
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-sm' 
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'
                }`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      value="monnify"
                      checked={paymentMethod === 'monnify'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'monnify')}
                      className="mt-1 mr-4 w-4 h-4 text-purple-600 focus:ring-purple-500 focus:ring-2"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-semibold text-gray-900 dark:text-white">Pay with Monnify</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Secure payments via Monnify gateway
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {(storeInfo?.StripeKey && storeInfo?.StripeSecrete) && (
                <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'stripe')}
                      className="mr-3 text-brand-primary focus:ring-brand-primary"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">Pay with Stripe</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Credit/debit cards, Apple Pay, Google Pay
                      </p>
                      {paymentMethod === 'stripe' && !storeInfo?.StripeKey && (
                        <p className="text-sm text-red-500 dark:text-red-400 mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                          Stripe payments are currently unavailable. Please choose another payment method or contact the store administrator.
                        </p>
                      )}
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Split Payment Disclaimer */}
          {showSeparateShipping && paymentMethod === 'bank' && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-yellow-50 dark:bg-yellow-900/20">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                    Important: Split Payment Instructions
                  </h4>
                  <div className="text-sm text-yellow-700 dark:text-yellow-300 space-y-2">
                    <p>
                      <strong>DO NOT combine payments!</strong> You must make separate payments:
                    </p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>Order amount: {formatPrice(orderTotal + vatAmount)} → Main account</li>
                      <li>Shipping fee: {formatPrice(shippingTotal)} → {storeInfo?.sepShippingAcn || 'Shipping account'}</li>
                    </ul>
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      ⚠️ Combined payments will NOT be refunded and your order will NOT be shipped!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 mt-auto">
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={onClose}
                className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl transition-all duration-200 font-medium hover:shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                disabled={(paymentMethod === 'stripe' && !storeInfo?.StripeKey) || isLoading}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:shadow-none flex items-center gap-2 min-w-[140px]"
              >
                {isLoading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
                {isLoading ? 'Processing...' : getButtonLabel()}
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}